import { useEffect, useState } from "react";
import * as compSettings from "./settings";
import { useLocation, useNavigate } from "react-router-dom";

const getBannerDimensions = (aspectRatio = 1, maxWidth = 0) => {
  const screenWidth = window.innerWidth;
  const width = maxWidth > 0 ? Math.min(screenWidth, maxWidth) : screenWidth;
  return { width, height: width * aspectRatio };
};

const SingleBanner = ({ id, extendedSettings, onClick }) => {
  const settings = {
    ...compSettings,
    ...extendedSettings?.[id],
  };
  const navigate = useNavigate()

  // Settings'ten değerleri al - prop'larla override edilebilir
  const banner = JSON.parse(settings?.banner?.value)[0];

  const aspectRatio = settings.aspectRatio.value;

  const backgroundColor = settings.backgroundColor.value;
  const borderColor = settings.borderColor.value;
  const shadowColor = settings.shadowColor.value;
  const overlayColor = settings.overlayColor.value;
  const marginX = settings.marginX.value + "px";
  const marginY = settings.marginY.value + "px";
  const paddingX = settings.paddingX.value + "px";
  const paddingY = settings.paddingY.value + "px";
  const borderRadius = settings.borderRadius.value + "px";
  const borderWidth = settings.borderWidth.value + "px";
  const shadowBlur = settings.shadowBlur.value + "px";
  const shadowOffsetX = settings.shadowOffsetX.value + "px";
  const shadowOffsetY = settings.shadowOffsetY.value + "px";
  const hoverScale = settings.hoverScale.value;
  const animationDuration = settings.animationDuration.value + "ms";
  const maxWidth = settings.maxWidth.value;
  const minHeight = settings.minHeight.value + "px";
  const overlayOpacity = settings.overlayOpacity.value;
  const imageBrightness = settings.imageBrightness.value;
  const imageContrast = settings.imageContrast.value;
  const imageSaturation = settings.imageSaturation.value;
  const showBorder = settings.showBorder.value;
  const showShadow = settings.showShadow.value;
  const showOverlay = settings.showOverlay.value;
  const hoverEffects = settings.hoverEffects.value;
  const clickable = settings.clickable.value;
  const responsive = settings.responsive.value;
  const centerImage = settings.centerImage.value;

  const imageFilters = settings.imageFilters.value;
  const objectFit = settings.objectFit.value;
  const textAlign = settings.textAlign.value;
  const hoverOverlayColor = settings.hoverOverlayColor.value;
  const loadingColor = settings.loadingColor.value;

  const [dimensions, setDimensions] = useState(
    getBannerDimensions(aspectRatio, maxWidth)
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!responsive) return;
    const handleResize = () =>
      setDimensions(getBannerDimensions(aspectRatio, maxWidth));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [aspectRatio, maxWidth, responsive]);

  if (!banner) return null;

  const getBoxShadow = () => {
    if (!showShadow) return "none";
    return `${shadowOffsetX} ${shadowOffsetY} ${shadowBlur} ${shadowColor}`;
  };

  const getImageFilter = () => {
    if (!imageFilters) return "none";
    return `brightness(${imageBrightness}%) contrast(${imageContrast}%) saturate(${imageSaturation}%)`;
  };

  const getTransform = () => {
    if (!hoverEffects || !isHovered) return "none";
    return `scale(${hoverScale})`;
  };

  const containerStyle = {
    width: responsive ? dimensions.width + "px" : "100%",
    height: responsive ? dimensions.height + "px" : "auto",
    minHeight: minHeight,
    maxWidth: maxWidth > 0 ? maxWidth + "px" : "none",
    marginLeft: marginX,
    marginRight: marginX,
    marginTop: marginY,
    marginBottom: marginY,
    paddingLeft: paddingX,
    paddingRight: paddingX,
    paddingTop: paddingY,
    paddingBottom: paddingY,
    backgroundColor: backgroundColor,
    border: showBorder ? `${borderWidth} solid ${borderColor}` : "none",
    borderRadius: borderRadius,
    boxShadow: getBoxShadow(),
    position: "relative",
    overflow: "hidden",
    cursor: clickable ? "pointer" : "default",
    textAlign: textAlign,
    transition: hoverEffects ? `all ${animationDuration} ease` : "none",
    transform: getTransform(),
  };

  const imageStyle = {
    width: "100%",
    height: "100%",
    objectFit: objectFit,
    objectPosition: centerImage ? "center" : "top left",
    filter: getImageFilter(),
    transition: hoverEffects ? `all ${animationDuration} ease` : "none",
    display: isLoaded ? "block" : "none",
  };

  const overlayStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor:
      isHovered && hoverEffects ? hoverOverlayColor : overlayColor,
    opacity: showOverlay ? overlayOpacity : 0,
    transition: hoverEffects ? `all ${animationDuration} ease` : "none",
    pointerEvents: "none",
  };

  const loadingStyle = {
    width: "100%",
    height: "100%",
    backgroundColor: loadingColor,
    display: isLoaded ? "none" : "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
    fontSize: "14px",
  };


  const onBannerClick = () => {
    navigate(`/category`, {
      state: { id: banner.id, name: banner.label },
    });
  };

  return (
    <div
      style={containerStyle}
      onClick={onBannerClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {!isLoaded && <div style={loadingStyle}>Yükleniyor...</div>}
      <img
        src={banner.img}
        alt={banner.altText || banner.name || "Banner"}
        style={imageStyle}
        draggable={false}
        onLoad={() => setIsLoaded(true)}
        onError={() => setIsLoaded(false)}
      />
      {showOverlay && <div style={overlayStyle} />}
    </div>
  );
};

export default SingleBanner;
